import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProducts, Product } from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import { useCart } from '@/hooks/useCart';

const collectionCards = [
  {
    name: 'Wind Spinners',
    image: 'https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/c4e1916781f99e894df361317e0bc41a.jpeg',
    path: '/collections/wind-spinners',
  },
  {
    name: 'Soapstone Animals',
    image: 'https://storage.readdy-site.link/project_files/8b56c003-1675-4c77-980a-9eb59e640845/50f806fd-2a64-4d4d-a878-8705c9132839_909427_f32f0d867c8f4ecd958d771f001bbe19mv2.jpg?v=0ca15b16bde757b3cdc7f5dab56e4bfb',
    path: '/collections/soapstone-animals',
  },
  {
    name: 'Wire Wrapped Stones',
    image: 'https://storage.readdy-site.link/project_files/8b56c003-1675-4c77-980a-9eb59e640845/ea3c4dba-5c2b-43ae-9cb3-6677806a47e5_909427_ee632c35f559489caf224c5c25c5405dmv2.png?v=fef261a9a13c2d74d1ccbc191461a9c6',
    path: '/collections/wire-wrapped-stones',
  },
  {
    name: 'Accessories',
    image: 'https://static.wixstatic.com/media/34789f_298255760caa41e1a9e28be56b08e8d9~mv2.jpg',
    path: '/collections/accessories',
  },
];

function ProductCard({ product }: { product: Product }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const image = product.media?.[0]?.url ?? '';
  const categoryName = product.product_categories?.name ?? '';

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Variant products must pick options first — send them to the detail page.
    if (product.hasVariants) { navigate(`/product/${product.id}`); return; }
    addItem({
      productId: product.id,
      skuId: null,
      skuLabel: null,
      name: product.name,
      price: product.price,
      image,
    });
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.hasVariants) { navigate(`/product/${product.id}`); return; }
    addItem({
      productId: product.id,
      skuId: null,
      skuLabel: null,
      name: product.name,
      price: product.price,
      image,
    });
    navigate('/checkout');
  };

  return (
    <div className="group bg-white rounded-lg overflow-hidden border border-[#E8E0D4] hover:shadow-md transition-shadow flex flex-col">
      <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-white p-3 md:p-4">
        {image && (
          <img
            src={image}
            alt={product.name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        )}
      </Link>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-base md:text-lg font-semibold text-[#1A1A1A] mb-1 leading-snug group-hover:text-[#4f528d] transition-colors text-center">
            {product.name}
          </h3>
        </Link>
        <p className="text-lg font-semibold text-[#2c2a4a] text-center mb-3">${product.price.toFixed(2)}</p>
        <div className="mt-auto grid grid-cols-2 gap-2">
          <button
            onClick={handleAddToCart}
            className="py-2 px-2 rounded-md border border-[#4f528d] text-[#4f528d] text-[11px] font-semibold uppercase tracking-tight hover:bg-[#4f528d] hover:text-white transition-colors whitespace-nowrap text-center"
          >
            Add to Cart
          </button>
          <button
            onClick={handleBuyNow}
            className="py-2 px-2 rounded-md bg-[#4f528d] text-white text-[11px] font-semibold uppercase tracking-tight hover:bg-[#3d4070] transition-colors whitespace-nowrap text-center"
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  useEffect(() => { document.title = 'Shop All | SpinWorld.Us'; }, []);
  const { products, loading, error, fetchProducts } = useProducts();
  const { categories } = useCategories();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sort, setSort] = useState('default');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    const categoryId = selectedCategory !== 'all' ? Number(selectedCategory) : undefined;
    fetchProducts(categoryId, search.trim() || undefined, sort);
  }, [selectedCategory, sort, fetchProducts]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const categoryId = selectedCategory !== 'all' ? Number(selectedCategory) : undefined;
    fetchProducts(categoryId, search.trim() || undefined, sort);
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSort('default');
    setSearch('');
    fetchProducts(undefined, undefined, 'default');
  };

  const activeFilterCount =
    (selectedCategory !== 'all' ? 1 : 0) +
    (sort !== 'default' ? 1 : 0) +
    (search.trim() ? 1 : 0);

  return (
    <main className="min-h-screen bg-[#f0f4f7]">
      {/* Page Header */}
      <section className="bg-[#2c2a4a] py-14 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Shop All</h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Browse our full collection of wind spinners, soapstone animals, wire wrapped stones, and accessories.
          </p>
        </div>
      </section>

      {/* Browse Collections */}
      <section className="py-8 md:py-12 bg-[#f0f4f7]">
        <div className="container-main">
          <div className="text-center mb-6 md:mb-8">
            <h2 className="font-serif text-xl md:text-2xl font-bold text-[#1A1A1A]">Shop by Collection</h2>
            <p className="text-sm text-[#8B7355] mt-1">Tap a collection to explore</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {collectionCards.map((col) => (
              <Link
                key={col.name}
                to={col.path}
                className="group block rounded-xl overflow-hidden border border-[#E8E0D4] bg-white hover:shadow-md transition-shadow"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={col.image}
                    alt={col.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-serif text-white text-base md:text-lg font-semibold drop-shadow">{col.name}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Products with Sidebar Filters */}
      <section className="py-6 md:py-10">
        <div className="container-main">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <aside className="w-full lg:w-64 shrink-0">
              <button
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                className="lg:hidden w-full flex items-center justify-between bg-white border border-[#E8E0D4] rounded-md px-4 py-3 text-sm font-medium text-[#1A1A1A] mb-3"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-[#4f528d] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileFiltersOpen ? 'rotate-180' : ''}`} />
              </button>

              <div className={`${mobileFiltersOpen ? 'block' : 'hidden'} lg:block bg-white rounded-lg border border-[#E8E0D4] p-5 space-y-6`}>
                {/* Search */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3 uppercase tracking-wider">Search</h4>
                  <form onSubmit={handleSearchSubmit} className="relative">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search products..."
                      className="w-full bg-[#FAF8F4] border border-[#D4C9B8] rounded-md py-2.5 pl-9 pr-3 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                    />
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#8B7355]" />
                  </form>
                </div>

                {/* Category */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3 uppercase tracking-wider">Category</h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="shop-category"
                        value="all"
                        checked={selectedCategory === 'all'}
                        onChange={() => setSelectedCategory('all')}
                        className="accent-[#4f528d]"
                      />
                      <span className="text-sm text-[#1A1A1A]">All Products</span>
                    </label>
                    {categories.map((cat) => (
                      <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="shop-category"
                          value={String(cat.id)}
                          checked={selectedCategory === String(cat.id)}
                          onChange={() => setSelectedCategory(String(cat.id))}
                          className="accent-[#4f528d]"
                        />
                        <span className="text-sm text-[#1A1A1A]">{cat.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="w-full py-2.5 text-sm text-[#4f528d] font-medium border border-[#4f528d] rounded-md hover:bg-[#4f528d] hover:text-white transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Sort Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <p className="text-sm text-[#8B7355]">
                  {products.length} product{products.length !== 1 ? 's' : ''}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="shop-sort" className="text-sm text-[#8B7355] whitespace-nowrap">Sort by:</label>
                  <select
                    id="shop-sort"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="bg-white border border-[#D4C9B8] rounded-md py-2 px-3 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B87333] cursor-pointer"
                  >
                    <option value="default">Featured</option>
                    <option value="price-asc">Price: Low to High</option>
                    <option value="price-desc">Price: High to Low</option>
                    <option value="newest">Newest</option>
                  </select>
                </div>
              </div>

              {/* Loading */}
              {loading && (
                <div className="text-center py-16">
                  <div className="w-10 h-10 border-3 border-[#B87333] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#8B7355]">Loading products...</p>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="text-center py-16">
                  <p className="text-red-600">Error loading products. Please try again.</p>
                </div>
              )}

              {/* Empty */}
              {!loading && !error && products.length === 0 && (
                <div className="text-center py-16">
                  <i className="ri-shopping-basket-line text-4xl text-[#D4C9B8] mb-3" />
                  <p className="text-[#8B7355] text-lg">No products found.</p>
                  <button
                    onClick={clearFilters}
                    className="mt-4 text-[#B87333] hover:underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )}

              {/* Grid */}
              {!loading && !error && products.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}